import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MultiSelect - generic multi-value selector with search and change-tracking
 *
 * Works for any selectable option: workspace members, tags, labels, or any
 * { value, label, sublabel? } shape. Composed from Radix Popover.
 * Follows the Protoss design system: semantic CSS variables, no hard-coded colors.
 *
 * Features:
 * - Text input for label/sublabel search
 * - Popover dropdown showing filtered options
 * - Chip tags for selected options with individual remove buttons
 * - Optional status list (Existing / Added / Removed) when originalValues is provided
 *   - Existing: in both originalValues and selectedValues
 *   - Added:    in selectedValues only (not in originalValues)
 *   - Removed:  in originalValues only (no longer selected)
 * - In create mode (no originalValues), selected items show "Added"
 * - loading prop: shows a disabled "Loading…" skeleton while options are being fetched
 * - Keyboard: type to filter, click to select/deselect, Backspace to remove last chip,
 *             Enter to select the sole remaining match
 *
 * Usage (create mode):
 * ```tsx
 * <MultiSelect
 *   options={members.map(m => ({
 *     value: m.uuid,
 *     label: m.displayName ?? m.email,
 *     sublabel: m.displayName ? m.email : undefined,
 *   }))}
 *   selectedValues={selectedUuids}
 *   onSelectionChange={setSelectedUuids}
 *   label="Domain Administrators"
 *   required
 * />
 * ```
 *
 * Usage (edit mode with status tracking):
 * ```tsx
 * <MultiSelect
 *   options={members.map(m => ({
 *     value: m.uuid,
 *     label: m.displayName ?? m.email,
 *     sublabel: m.displayName ? m.email : undefined,
 *   }))}
 *   selectedValues={selectedUuids}
 *   originalValues={originalAdminUuids}
 *   onSelectionChange={setSelectedUuids}
 *   label="Domain Administrators"
 * />
 * ```
 */
import * as Popover from '@radix-ui/react-popover';
import * as React from 'react';
import { RequiredLabel } from './RequiredLabel';
const STATUS_LABELS = {
    existing: 'Existing',
    added: 'Added',
    removed: 'Removed',
    inherited: 'Inherited',
};
export function MultiSelect({ options, selectedValues, originalValues, lockedValues = [], onSelectionChange, id, name, label, placeholder = 'Type to search…', disabled = false, loading = false, required = false, error, helperText, suppressErrorMessage = false, showRequiredText = false, showStatusList = true, annotation, annotationValues, onAnnotationChange, size, }) {
    const inputId = id || React.useId();
    const listboxId = `${inputId}-listbox`;
    const [query, setQuery] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [openAnnotationFor, setOpenAnnotationFor] = React.useState(null);
    const inputRef = React.useRef(null);
    const controlRef = React.useRef(null);
    const isEditMode = originalValues !== undefined;
    const isInteractive = !disabled && !loading;
    const selectedSet = React.useMemo(() => new Set(selectedValues), [selectedValues]);
    const lockedSet = React.useMemo(() => new Set(lockedValues), [lockedValues]);
    const originalSet = React.useMemo(() => new Set(isEditMode ? (originalValues ?? []) : []), [isEditMode, originalValues]);
    const optionByValue = React.useMemo(() => {
        const map = new Map();
        for (const o of options) {
            map.set(o.value, o);
        }
        return map;
    }, [options]);
    const filteredOptions = React.useMemo(() => {
        const q = query.toLowerCase().trim();
        return options.filter((o) => {
            if (selectedSet.has(o.value))
                return false;
            if (lockedSet.has(o.value))
                return false;
            if (!q)
                return true;
            return o.label.toLowerCase().includes(q) || (o.sublabel ?? '').toLowerCase().includes(q);
        });
    }, [options, selectedSet, lockedSet, query]);
    const handleSelect = (value) => {
        if (!selectedSet.has(value)) {
            onSelectionChange([...selectedValues, value]);
        }
        setQuery('');
        inputRef.current?.focus();
    };
    const handleRemove = (value) => {
        onSelectionChange(selectedValues.filter((v) => v !== value));
        inputRef.current?.focus();
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
            e.preventDefault();
        }
        if (e.key === 'Backspace' && query === '' && selectedValues.length > 0) {
            onSelectionChange(selectedValues.slice(0, -1));
        }
        if (e.key === 'Enter') {
            if (filteredOptions.length === 1 && filteredOptions[0]) {
                handleSelect(filteredOptions[0].value);
                e.preventDefault();
            }
        }
    };
    const statusEntries = React.useMemo(() => {
        // Inherited (locked) entries always appear first
        const lockedEntries = [];
        for (const value of lockedValues) {
            const option = optionByValue.get(value);
            if (!option)
                continue;
            lockedEntries.push({ option, status: 'inherited' });
        }
        const allValues = new Set([...selectedValues, ...(originalValues ?? [])]);
        const entries = [];
        for (const value of allValues) {
            const option = optionByValue.get(value);
            if (!option)
                continue;
            let status;
            if (isEditMode) {
                if (selectedSet.has(value) && originalSet.has(value)) {
                    status = 'existing';
                }
                else if (selectedSet.has(value)) {
                    status = 'added';
                }
                else {
                    status = 'removed';
                }
            }
            else {
                status = 'added';
            }
            entries.push({ option, status });
        }
        const ORDER = {
            existing: 0,
            added: 1,
            removed: 2,
            inherited: -1,
        };
        entries.sort((a, b) => ORDER[a.status] - ORDER[b.status]);
        return [...lockedEntries, ...entries];
    }, [
        lockedValues,
        selectedValues,
        originalValues,
        optionByValue,
        isEditMode,
        selectedSet,
        originalSet,
    ]);
    return (_jsxs("div", { className: "multi-select", children: [(label || helperText) && (_jsxs("div", { className: "multi-select__header", children: [label && (_jsx("label", { htmlFor: inputId, className: "multi-select__label", children: _jsx(RequiredLabel, { label: label, required: required, showRequiredText: showRequiredText || Boolean(error) }) })), helperText && _jsx("span", { className: "multi-select__helper", children: helperText })] })), _jsxs(Popover.Root, { open: open, onOpenChange: setOpen, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsxs("div", { ref: controlRef, className: [
                                'multi-select__control',
                                size === 'comfortable' ? 'multi-select__control--comfortable' : '',
                                error ? 'multi-select__control--error' : '',
                                !isInteractive ? 'multi-select__control--disabled' : '',
                                open ? 'multi-select__control--open' : '',
                            ]
                                .filter(Boolean)
                                .join(' '), onClick: () => {
                                if (isInteractive) {
                                    inputRef.current?.focus();
                                    setOpen(true);
                                }
                            }, role: "combobox", "aria-expanded": open, "aria-haspopup": "listbox", "aria-controls": listboxId, children: [!loading &&
                                    lockedValues.map((value) => {
                                        const option = optionByValue.get(value);
                                        if (!option)
                                            return null;
                                        return (_jsx("span", { className: "multi-select__chip multi-select__chip--locked", children: _jsx("span", { className: "multi-select__chip-label", children: option.label }) }, value));
                                    }), !loading &&
                                    selectedValues.map((value) => {
                                        const option = optionByValue.get(value);
                                        if (!option)
                                            return null;
                                        return (_jsxs("span", { className: "multi-select__chip", children: [_jsx("span", { className: "multi-select__chip-label", children: option.label }), !disabled && (_jsx("button", { type: "button", className: "multi-select__chip-remove", onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleRemove(value);
                                                    }, "aria-label": `Remove ${option.label}`, children: "\u00D7" }))] }, value));
                                    }), _jsx("input", { ref: inputRef, id: inputId, name: name, type: "text", className: "multi-select__input", value: loading ? '' : query, placeholder: loading ? 'Loading…' : selectedValues.length === 0 ? placeholder : '', disabled: !isInteractive, autoComplete: "off", "aria-autocomplete": "list", "aria-controls": listboxId, onChange: (e) => {
                                        setQuery(e.target.value);
                                        setOpen(true);
                                    }, onFocus: () => {
                                        if (isInteractive)
                                            setOpen(true);
                                    }, onKeyDown: handleKeyDown }), _jsxs("span", { className: "multi-select__multi-badge", "aria-hidden": "true", children: [_jsx("span", { className: "multi-select__multi-badge-head", children: "mul" }), _jsx("span", { className: "multi-select__multi-badge-tail", children: "ti" })] })] }) }), !loading && (_jsx(Popover.Portal, { children: _jsx(Popover.Content, { className: "multi-select__dropdown", align: "start", sideOffset: 4, onOpenAutoFocus: (e) => e.preventDefault(), onFocusOutside: (e) => {
                                // Keep open when focus stays within our control (e.g. input, chip buttons).
                                // Let close naturally when focus moves to a truly external element.
                                if (controlRef.current?.contains(e.target)) {
                                    e.preventDefault();
                                }
                            }, onInteractOutside: (e) => {
                                // Keep open when the pointer interaction is within our control div
                                // (clicking the input, chips, or search area should never dismiss).
                                if (controlRef.current?.contains(e.target)) {
                                    e.preventDefault();
                                }
                            }, children: _jsx("ul", { id: listboxId, className: "multi-select__option-list", role: "listbox", "aria-label": label ?? 'Options', children: filteredOptions.length === 0 ? (_jsx("li", { className: "multi-select__option-empty", children: query ? 'No matching options' : 'All options selected' })) : (filteredOptions.map((option) => (_jsxs("li", { className: "multi-select__option", role: "option", "aria-selected": false, onMouseDown: (e) => e.preventDefault(), onClick: () => handleSelect(option.value), onKeyDown: (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            handleSelect(option.value);
                                            e.preventDefault();
                                        }
                                    }, tabIndex: 0, children: [_jsx("span", { className: "multi-select__option-name", children: option.label }), option.sublabel && (_jsx("span", { className: "multi-select__option-email", children: option.sublabel }))] }, option.value)))) }) }) }))] }), error && !suppressErrorMessage && (_jsx("p", { className: "multi-select__error", role: "alert", children: error })), !loading && (showStatusList || !!annotation) && statusEntries.length > 0 && (_jsx("ul", { className: [
                    'multi-select__status-list',
                    annotation ? 'multi-select__status-list--annotation' : '',
                ]
                    .filter(Boolean)
                    .join(' '), "aria-label": "Selection changes", children: statusEntries
                    .filter((entry) => !annotation || entry.status !== 'removed')
                    .map(({ option, status }) => {
                    if (annotation) {
                        const currentVal = annotationValues?.[option.value] ?? annotation.default;
                        const annotOpt = annotation.options.find((o) => o.value === currentVal);
                        return (_jsxs("li", { className: "multi-select__status-item multi-select__status-item--annotation", children: [_jsx("span", { className: "multi-select__status-name", children: option.label }), _jsxs(Popover.Root, { open: openAnnotationFor === option.value, onOpenChange: (isOpen) => setOpenAnnotationFor(isOpen ? option.value : null), children: [_jsx(Popover.Trigger, { asChild: true, children: _jsx("button", { type: "button", className: `multi-select__annotation-badge multi-select__annotation-badge--${annotOpt?.color ?? currentVal}`, disabled: disabled, children: annotOpt?.label ?? currentVal }) }), _jsx(Popover.Portal, { children: _jsx(Popover.Content, { className: "multi-select__annotation-popover", align: "start", sideOffset: 4, onOpenAutoFocus: (e) => e.preventDefault(), children: annotation.options.map((opt) => (_jsx("button", { type: "button", className: [
                                                        'multi-select__annotation-option',
                                                        `multi-select__annotation-option--${opt.color ?? opt.value}`,
                                                        opt.value === currentVal
                                                            ? 'multi-select__annotation-option--selected'
                                                            : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' '), onClick: () => {
                                                        onAnnotationChange?.(option.value, opt.value);
                                                        setOpenAnnotationFor(null);
                                                    }, children: opt.label }, opt.value))) }) })] })] }, option.value));
                    }
                    return (_jsxs("li", { className: "multi-select__status-item", children: [_jsxs("div", { className: "multi-select__status-item-info", children: [_jsx("span", { className: "multi-select__status-name", children: option.label }), option.sublabel && (_jsx("span", { className: "multi-select__status-email", children: option.sublabel }))] }), _jsx("span", { className: `multi-select__status-badge multi-select__status-badge--${status}`, children: STATUS_LABELS[status] })] }, option.value));
                }) }))] }));
}
//# sourceMappingURL=MultiSelect.js.map